import React, { useCallback, useEffect, useState } from 'react';

import { Box, Checkbox } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import Carousel from 'components/Carousel';
import { StyledAvatar } from 'components/avatars';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import {
  ColoredCircularProgress,
  StyledBox,
  StyledButton,
  StyledGrid,
  StyledPaper,
  StyledTypography,
} from 'styles/common';

import ReviewPendingImage from 'assets/images/review-result-pending.jpg';

import { REVIEW_FORMS_TYPE_MAP, REVIEW_TEXTS, REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { routes } from 'constants/routes';
import theme from 'constants/theme';

import { getReviewProviders } from 'services/performanceReview';
import { showErrorSnackbar } from 'services/snackbar';

import { useReview } from '../ReviewContext';
import BehaviorForm from './BehaviorForm';
import GoalForm from './GoalForm';
import QAForm from './QAForm';
import RatingAndSpiderChart from './RatingAndSpiderChart';
import { showErrorMessage, showLoader, showNoData } from './common';

const ALL_AVAILABLE_FORMS = ['GOAL', 'COMPETENCY', 'QNA'];

const ImageContainer = styled.div`
  max-width: 50%;
  img {
    width: 100%;
  }
`;

const ElongatedButton = styled(StyledButton)`
  min-width: 8rem;
  width: 10rem;
  padding-top: 0.875rem;
  padding-bottom: 0.875rem;
`;

const BorderedStyledPaper = styled(StyledPaper)`
  margin-bottom: 1.35rem;
  border: 1px solid #008f2f;
  padding-bottom: 0.85rem;
  padding-top: 0.25rem;
`;

const StyledCheckIcon = styled(CheckIcon)`
  color: #008f2f;
  vertical-align: top;
  margin: 1.25rem 0 0 1rem;
`;

function AcknowledgeControl({
  autoNavigate,
  selectedReviewInfo,
  selectedProvider,
  confirmAcknowledgement,
  setconfirmAcknowledgement,
  acceptReview,
  sendingResponse,
}) {
  useEffect(() => {
    if (autoNavigate) {
      const buttonElement = document.getElementsByClassName('review-form-action-button')[0];
      if (buttonElement) {
        buttonElement.scrollIntoView();
      }
    }
  });
  return (
    selectedReviewInfo.reviewProviderType === REVIEW_PROVIDER_TYPES.SELF &&
    selectedReviewInfo.isPublished &&
    selectedProvider.type === REVIEW_PROVIDER_TYPES.MANAGER &&
    !selectedProvider.hasAcknowledgedReview && (
      <>
        <StyledPaper
          flexcontainer
          justifycontent="flex-start"
          alignitems="center"
          paddingTop={`${theme.spacing(1)}rem`}
          paddingLeft={`${theme.spacing(1)}rem`}
          paddingBottom={`${theme.spacing(1)}rem`}
          topGutter={3}
          bottomGutter={3}
        >
          <Checkbox
            color="primary"
            checked={confirmAcknowledgement}
            onChange={() => {
              setconfirmAcknowledgement((prev) => !prev);
            }}
          />
          <StyledTypography variant="body1" leftGutter={1}>
            Your manager has shared this review cycle&apos;s feedback with you
          </StyledTypography>
        </StyledPaper>

        <StyledBox display="flex" alignItems="center" justifyContent="flex-end" marginTop="1rem" padding="1rem">
          <ElongatedButton
            variant="outlined"
            size="large"
            onClick={() => {
              acceptReview();
            }}
            customColor={confirmAcknowledgement ? 'primary' : 'white'}
            disableRipple
            disableFocusRipple
            disabled={!confirmAcknowledgement}
            className="review-form-action-button"
          >
            {sendingResponse ? (
              <ColoredCircularProgress color="white" size="tiny" />
            ) : (
              <StyledTypography
                variant="body1"
                color={confirmAcknowledgement ? 'white' : 'textSecondary'}
                clickable
                weight={600}
              >
                Accept Review
              </StyledTypography>
            )}
          </ElongatedButton>
        </StyledBox>
      </>
    )
  );
}

function EmployeeHeaderBox({
  employee,
  review,
  isAccessible,
  accessReasonText,
  selectedProvider,
  setAutoNavigate,
  selectReviewProvider,
}) {
  const isActive = review.providerEmployeeId
    ? String(review.providerEmployeeId) === String(selectedProvider.id)
    : String(selectedProvider.responseId) === String(review.id);

  return (
    <React.Fragment key={`rp-header-${review.providerEmployeeId}`}>
      <StyledBox
        flex={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="2rem"
        style={{ cursor: 'pointer', borderRight: '1px solid #e4e4e4' }}
        bgcolor={isActive ? theme.palette.background.lightBlue : 'inherit'}
        onClick={() => {
          // on manually changing the review the auto-navigation should turn off
          setAutoNavigate(false);
          selectReviewProvider(
            review.providerEmployeeId,
            review.providerType,
            review.submissionStatus === 'ACKNOWLEDGED',
            review.submissionStatus === 'SUBMITTED',
            isAccessible,
            review.id,
            accessReasonText
          );
        }}
      >
        <StyledAvatar
          border="1px solid lightgrey"
          size="small"
          alt="Profile Image"
          src={employee ? employee.profilePhotoSrc : ''}
          variant="circle"
          rightGutter={2}
          leftGutter={1}
        />
        <StyledGrid item direction="column">
          <StyledTypography variant="body1" weight="600">
            {employee ? employee.displayName : 'Anonymous'}
          </StyledTypography>
          <StyledTypography variant="body2" color="textSecondary">
            {review.providerType.toProperCase()}
          </StyledTypography>
        </StyledGrid>
      </StyledBox>
    </React.Fragment>
  );
}

const ResponseProviderHeader = ({
  employeeId,
  reviewId,
  selectReviewProvider,
  reloadProviders = 0,
  autoNavigate = false,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [reviewProviders, setReviewProviders] = useState(null);

  const fetchReviewProviders = () => {
    setLoading(true);
    getReviewProviders({
      employeeId,
      reviewId,
    })
      .then((res) => {
        setReviewProviders(res);
        const current = res.filter((r) => r.employee && r.employee.id.toString() === employeeId.toString())[0];

        if (autoNavigate) {
          const managerReview = res.filter((r) => r.review.providerType === 'MANAGER')[0];
          selectReviewProvider(
            managerReview.employee.id,
            managerReview.review.providerType,
            managerReview.review.submissionStatus === 'ACKNOWLEDGED',
            managerReview.review.submissionStatus === 'SUBMITTED',
            managerReview.isAccessible,
            managerReview.accessReasonText
          );
        } else if (current) {
          selectReviewProvider(
            employeeId,
            current.review.providerType,
            current.review.submissionStatus === 'ACKNOWLEDGED',
            current.review.submissionStatus === 'SUBMITTED',
            current.isAccessible,
            current.accessReasonText
          );
        } else {
          const firstProvider = res?.[0];
          selectReviewProvider(
            firstProvider?.employee.id,
            firstProvider?.review.providerType,
            firstProvider?.review.submissionStatus === 'ACKNOWLEDGED',
            firstProvider?.review.submissionStatus === 'SUBMITTED',
            firstProvider?.isAccessible,
            firstProvider?.accessReasonText
          );
        }
      })
      .catch((err) => {
        console.log(err);
        dispatch(showErrorSnackbar(err.message || 'some error occurred while fetching review provider list.'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(fetchReviewProviders, [reloadProviders]);

  return (
    <StyledPaper flexcontainer padding={0} bottomGutter={3}>
      {loading && showLoader()}
      {!loading &&
        reviewProviders === null &&
        showErrorMessage({ errorText: REVIEW_TEXTS.REVIEW_PROVIDER_HEADER.ERROR_MSG })}
      {!loading &&
        reviewProviders !== null &&
        (reviewProviders.length === 0 ? (
          showNoData({ infoText: REVIEW_TEXTS.REVIEW_PROVIDER_HEADER.NO_DATA })
        ) : (
          <Carousel slidesToShow={reviewProviders.length > 4 ? 4.3 : reviewProviders.length} slidesToScroll={4}>
            {reviewProviders.map(({ employee, review, deadline, isAccessible, accessReasonText }, index) => (
              <EmployeeHeaderBox
                employee={employee}
                review={review}
                deadline={deadline}
                index={index}
                isAccessible={isAccessible}
                accessReasonText={accessReasonText}
              />
            ))}
          </Carousel>
        ))}
    </StyledPaper>
  );
};

const ThankYouNotice = ({ employeeName, reviewCycleName }) => (
  <BorderedStyledPaper>
    <span>
      <StyledCheckIcon />
    </span>
    <span style={{ display: 'inline-block' }}>
      <StyledTypography topGutter={2.5} bottomGutter={1.5} leftGutter={1.5} color="#008f2f" variant="h6">
        {`Thank you! ${employeeName} appreciates your feedback for ${reviewCycleName}`}
      </StyledTypography>
    </span>
  </BorderedStyledPaper>
);

const ManagerPlaceholderComp = ({ accessReasonText }) => (
  <StyledPaper>
    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
      <ImageContainer color="textSecondaryLight">
        <img alt="Review Pending" src={ReviewPendingImage} />
      </ImageContainer>
      <StyledTypography topGutter={3} bottomGutter={3} variant="h6" align="center" color="textSecondaryLight">
        {/* Waiting for your manager to complete your review. <br /> */}
        {accessReasonText} <br />
        Go back to review <Link to={routes.performanceReview.index}>homepage</Link>.
      </StyledTypography>
    </Box>
  </StyledPaper>
);

const SUMMARY_COMPONENT = {
  [REVIEW_FORMS_TYPE_MAP.GOAL]: ({ employeeId, reviewId, providerId, responseId, isShowGoalWeightage }) => (
    <GoalForm
      employeeId={employeeId}
      providerId={providerId}
      reviewId={reviewId}
      isManagerView={false}
      isSummaryView
      responseId={responseId}
      isShowGoalWeightage={isShowGoalWeightage}
    />
  ),
  [REVIEW_FORMS_TYPE_MAP.COMPETENCY]: ({ employeeId, reviewId, providerId, responseId }) => (
    <BehaviorForm
      employeeId={employeeId}
      providerId={providerId}
      reviewId={reviewId}
      isManagerView={false}
      isSummaryView
      responseId={responseId}
    />
  ),
  [REVIEW_FORMS_TYPE_MAP.QNA]: ({ employeeId, reviewId, providerId, responseId }) => (
    <QAForm
      employeeId={employeeId}
      providerId={providerId}
      reviewId={reviewId}
      isManagerView={false}
      isSummaryView
      responseId={responseId}
    />
  ),
};

const ResponseSummary = ({
  employeeId,
  reviewId,
  autoNavigate = false,
  setAutoNavigate,
  isShowGoalWeightage = false,
}) => {
  const { id } = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const {
    state: { selectedReviewInfo },
  } = useReview();

  // updaing providerApiCallNumber will result in re-fetching provider list.
  const [providerApiCallNumber] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState({
    id,
    type: selectedReviewInfo.reviewProviderType,
    hasAcknowledgedReview: null,
    hasSubmittedReview: null,
    responseId: null,
    isAccessible: false,
    accessReasonText: null,
  });

  const selectReviewProvider = useCallback(
    (
      providerId,
      providerType,
      // eslint-disable-next-line default-param-last
      hasAcknowledgedReview = false,
      // eslint-disable-next-line default-param-last
      hasSubmittedReview = false,
      // eslint-disable-next-line default-param-last
      isAccessible = false,
      responseId,
      accessReasonText
    ) => {
      setSelectedProvider({
        id: providerId,
        type: providerType,
        hasAcknowledgedReview,
        hasSubmittedReview,
        responseId,
        isAccessible,
        accessReasonText,
      });
    },
    []
  );

  const renderSummaryComponent = () => (
    <>
      {ALL_AVAILABLE_FORMS.map((formId) =>
        SUMMARY_COMPONENT[formId] ? (
          <StyledPaper bottomGutter={3}>
            {SUMMARY_COMPONENT[formId]({
              reviewId,
              employeeId,
              providerId: selectedProvider.id,
              responseId: selectedProvider.responseId,
              isShowGoalWeightage,
            })}
          </StyledPaper>
        ) : null
      )}
    </>
  );

  return (
    <StyledBox width="100%" topGutter={2}>
      {
        // Thanks you notice
        (selectedReviewInfo.reviewProviderType === REVIEW_PROVIDER_TYPES.PEER ||
          selectedReviewInfo.reviewProviderType === REVIEW_PROVIDER_TYPES.UPWARD) &&
          !selectedReviewInfo.isPublished && (
            <ThankYouNotice
              employeeName={selectedReviewInfo.employeeName}
              reviewCycleName={selectedReviewInfo.reviewCycleName}
            />
          )
      }

      {
        // Rating and Spider chart
        selectedReviewInfo.isShowRatingPanel ? (
          <RatingAndSpiderChart
            employeeId={employeeId}
            reviewId={reviewId}
            isReviewPublished={selectedReviewInfo.isPublished}
          />
        ) : null
      }

      <ResponseProviderHeader
        employeeId={employeeId}
        reviewId={reviewId}
        selectReviewProvider={selectReviewProvider}
        selectedProvider={selectedProvider}
        reloadProviders={providerApiCallNumber}
        autoNavigate={autoNavigate}
        setAutoNavigate={setAutoNavigate}
      />
      {selectedProvider.isAccessible ? (
        <>
          {renderSummaryComponent()}
          <AcknowledgeControl />
        </>
      ) : (
        <ManagerPlaceholderComp
          providerType={selectedProvider.type}
          accessReasonText={selectedProvider.accessReasonText}
        />
      )}
    </StyledBox>
  );
};

export default ResponseSummary;
